// Sugalarm — top-level login flow.
//
// Steps:
//   provider  → ProviderPicker (also entry point)
//   signin    → SignInForm   — POST /api/auth/login or /register
//   connect   → CGMConnectForm — POST /api/cgm/connect
//   done      → Connected   — brief success card before /now
//
// If the user is already signed in *and* already has a CGM linked, we
// skip the picker entirely and route to /now.
//
// Cycle 464: a signed-in account that owes the "Start your free month" step
// sees it FIRST, before the provider picker / CGM connect (and therefore
// before the cycle-316 alarm tour, which holds on the same gate). First-run
// order: create account → free month → connect CGM → alarm tour → pair
// kiosk. The step is an ordinary in-flow step of this column.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../hooks/useAuth";
import { useFrameVariant } from "../../hooks/useFrameVariant";
import { useSubscriptionStepGate } from "../../hooks/useSubscription";
import { SubscriptionStep } from "../onboarding/SubscriptionStep";
import { CGMConnectForm } from "./CGMConnectForm";
import { Connected } from "./Connected";
import { ProviderPicker } from "./ProviderPicker";
import type { Provider } from "./providers";
import { findProvider } from "./providers";
import { ShieldGlowSurface } from "./ShieldGlowSurface";
import { SignInForm } from "./SignInForm";

type Step = "provider" | "signin" | "connect" | "done";

interface Props {
  auth: ReturnType<typeof useAuth>;
  /** Cycle 453.1: rendered inside the kiosk frame (the signed-in `/login`).
   *  Fill the space the frame gives it instead of `100vh`, so the frame's
   *  No Data banner is never pushed below the stage; overflow scrolls. */
  fitFrame?: boolean;
  /** Cycle 464: an alarm (or No Data alarm) is live — the free-month step
   *  renders nothing until it clears. False on the signed-out route, which
   *  has no alarm layer and never owes the step. */
  alarmLive?: boolean;
}

export function LoginScreen({ auth, fitFrame = false, alarmLive = false }: Props) {
  const nav = useNavigate();
  // Cycle 170: the web surface lands on the command-station Dashboard;
  // kiosk + mobile keep the existing /now landing.
  const variant = useFrameVariant();
  const home = variant === "web" ? "/dashboard" : "/now";
  const [provider, setProvider] = useState<Provider | null>(null);
  const [mode, setMode] = useState<"signin" | "register">("signin");
  const [step, setStep] = useState<Step>(() =>
    auth.status === "signedIn" ? (auth.user?.cgm_provider ? "done" : "provider") : "provider",
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backfillCount, setBackfillCount] = useState<number>(0);
  const signedIn = auth.status === "signedIn";
  const stepGate = useSubscriptionStepGate(signedIn, auth.user, variant === "kiosk");
  // Not over a CGM form someone is already typing in (review round 2, L1).
  const showSubscriptionStep =
    signedIn && stepGate.owed && stepGate.subscription != null && step !== "connect";
  // While the step is owed, or its answer is loading, no other first-run
  // screen renders under it — EXCEPT during a live alarm. ⚠ THE ALARM WINS
  // THE GATE (safety review V1): `/login` is exempt from the alarm route pin
  // and from the off-Now control, so holding a CGM-linked account here
  // during an alarm would leave the alarm sounding over a blank page with no
  // Snooze or Stop. During an alarm nothing is held: the effect below sends
  // a CGM-linked account home to `/now`, where the controls are, and the
  // step (not stamped) comes back on the next first-run screen or Settings.
  // Review round 2 (L1): once someone has moved on to typing CGM credentials
  // (possible only while an alarm released the hold), an alarm clearing never
  // re-holds and unmounts that form; the step waits for the next first run
  // or Settings.
  const holdFirstRun =
    signedIn && !alarmLive && step !== "connect" && (stepGate.owed || stepGate.pending);

  // Once signed in with a CGM, leave login — unless the free-month step is
  // owed or its answer is still loading (cycle 464).
  useEffect(() => {
    if ((stepGate.owed || stepGate.pending) && !alarmLive) return;
    if (auth.status === "signedIn" && auth.user?.cgm_provider) {
      // During a live alarm the escape goes to `/now`, where the alarm
      // controls are — never the web's `/dashboard` home (astra MEDIUM).
      nav(alarmLive ? "/now" : home, { replace: true });
    }
  }, [auth.status, auth.user, nav, home, stepGate.owed, stepGate.pending, alarmLive]);

  // If user is signed in but has no CGM, jump straight to provider/connect.
  useEffect(() => {
    if (auth.status === "signedIn" && !auth.user?.cgm_provider) {
      setStep((s) => (s === "signin" ? "provider" : s));
    }
  }, [auth.status, auth.user]);

  const onPick = (p: Provider) => {
    setProvider(p);
    setStep(auth.status === "signedIn" ? "connect" : "signin");
  };

  const onAuth = async (
    email: string,
    password: string,
    displayName?: string,
    remember?: boolean,
  ) => {
    setBusy(true);
    setError(null);
    const result =
      mode === "signin"
        ? await auth.signIn(email, password, remember)
        : await auth.signUp(email, password, displayName ?? email.split("@")[0] ?? "");
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    // After auth: the signed-in screen takes over (cycle 464). Its effect
    // routes a CGM-linked account home once the free-month gate is clear;
    // navigating home from here would skip the step on a first sign-in.
    setStep(provider ? "connect" : "provider");
  };

  const onConnected = async (result: { backfill_count?: number }) => {
    setBackfillCount(result.backfill_count ?? 0);
    await auth.refreshUser();
    setStep("done");
  };

  // Cycle 152: an alternate method (Google / Apple / passkey) returned a
  // TokenPair — adopt it through the one session path, then route the
  // same way the password path does.
  const onAltSuccess = (data: Parameters<typeof auth.adoptSession>[0]) => {
    setError(null);
    auth.adoptSession(data);
    // Cycle 464: as `onAuth` — the signed-in screen's effect routes home.
    setStep(provider ? "connect" : "provider");
  };

  // Cycle 475: the first "Connect your CGM" step wears the Shield Glow
  // surface — and ONLY that step, exactly when the pre-475 tree below would
  // have drawn the picker alone (not under a held first run, not beside the
  // free-month step). Sign-in, connect and connected, and the held blank
  // column, fall through to the unchanged tree. The callbacks are the same
  // ones the default picker received.
  if (!holdFirstRun && step === "provider" && !showSubscriptionStep) {
    return (
      <ShieldGlowSurface layout={variant === "mobile" ? "phone" : "tablet"} fitFrame={fitFrame}>
        <ProviderPicker
          presentation="onboarding"
          onPick={onPick}
          onSkip={() => setStep("signin")}
          showPairDevice
          onPairDevice={() => nav("/pair-device")}
        />
      </ShieldGlowSurface>
    );
  }

  // Cycle 457: inside the kiosk frame (`fitFrame`) the column is laid out
  // to FIT ABOVE the No Data banner, which on the signed-in /login sits at
  // the bottom of the frame (cycle 453.1). Two parts:
  //   * the content is centred by AUTO MARGINS on an inner wrapper, not by
  //     `justify-content: center` — when it fits it centres exactly as
  //     before, and when it does not it starts at the top and scrolls
  //     inside this column, instead of spilling out of BOTH ends with its
  //     bottom (the "Skip for now" line) running under the banner;
  //   * on the kiosk the lockup is 200 px wide (was 320) with less padding,
  //     and the encryption line is IN FLOW under the content (elsewhere it
  //     stays pinned to the bottom edge), so it can never sit on the "Skip
  //     for now" line; if anything falls below the fold it is that
  //     informational line, never a control.
  // The banner itself is untouched.
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: fitFrame ? "flex-start" : "center",
        padding: fitFrame ? "8px 36px 8px" : "24px 36px",
        position: "relative",
        ...(fitFrame ? { minHeight: 0, overflowY: "auto" as const } : { minHeight: "100vh" }),
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(circle at 50% 30%, var(--accent-soft), transparent 55%)",
        }}
      />

      {/* Cycle 57.2: the login screen wears the full brand lockup rather
          than the icon + text combo. Cycle 396 swaps it to
          `sugar-alarm-lockup.png` for the "Sugar Alarm" rebrand — the
          outgoing bitmap was 3.075:1 and the new one is 2.759:1, so at
          the unchanged 320px cap the banner grows from ~104px to ~116px
          tall. That is inside the room the form already had, so the
          width constraint below is deliberately untouched. */}
      <div
        data-testid="login-content"
        style={{
          position: "relative",
          marginTop: "auto",
          marginBottom: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
      <img
        src="/sugar-alarm-lockup.png"
        alt="Sugar Alarm"
        style={{
          position: "relative",
          width: fitFrame ? 200 : "min(320px, 80vw)",
          height: "auto",
          display: "block",
          marginBottom: fitFrame ? 12 : 24,
        }}
      />

      {showSubscriptionStep && stepGate.subscription != null && auth.user != null ? (
        <SubscriptionStep
          subscription={stepGate.subscription}
          accountId={auth.user.id}
          alarmLive={alarmLive}
          // Nothing to do here: finishing notes the step done, so the gate
          // releases — a CGM-linked account is routed home by the effect
          // above, everyone else sees the step they were already on.
          onDone={() => undefined}
        />
      ) : null}

      {!holdFirstRun && step === "provider" && (
        <ProviderPicker
          onPick={onPick}
          onSkip={() => setStep("signin")}
          // Cycle 331: lets someone setting up this device pair it to a
          // household already signed in on another phone, instead of
          // connecting a fresh CGM here. Only this — the first-run
          // onboarding CGM-connect step — offers it; the Settings
          // CgmConnectModal and per-member Members-pane pickers don't.
          showPairDevice
          onPairDevice={() => nav("/pair-device")}
        />
      )}

      {!holdFirstRun && step === "signin" && (
        <SignInForm
          mode={mode}
          onSubmit={onAuth}
          onSwitchMode={setMode}
          onForgotPassword={() => nav("/forgot-password")}
          onAltSuccess={onAltSuccess}
          onAltError={setError}
          error={error}
          busy={busy}
        />
      )}

      {!holdFirstRun && step === "connect" && provider && (
        <CGMConnectForm
          provider={provider}
          onConnected={onConnected}
          onChangeProvider={() => setStep("provider")}
        />
      )}

      {!holdFirstRun && step === "done" && (
        <Connected
          provider={provider ?? findProvider("dexcom")}
          backfillCount={backfillCount}
          onContinue={() => nav(home, { replace: true })}
        />
      )}
      </div>

      <div
        style={{
          ...(fitFrame
            ? { position: "relative" as const, marginTop: 10, flexShrink: 0 }
            : { position: "absolute" as const, bottom: 14, left: 0, right: 0 }),
          textAlign: "center",
          fontSize: "var(--type-floor)",
          color: "var(--ink-3)",
          letterSpacing: "0.04em",
        }}
      >
        Your data is encrypted at rest. Sugar Alarm never sells health data.
      </div>
    </div>
  );
}
